import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef } from 'react';
import { getTeamToken, isTeamMemberAuthenticated, clearTeamAuth } from '../services/teamAuthService';

const apiClient = axios.create({
    baseURL: process.env.REACT_APP_SERVER,
    timeout: 350000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if interceptors have been set up
let interceptorsInitialized = false;

function useAuthenticatedAxios() {
  const { getAccessTokenSilently, loginWithRedirect, isAuthenticated } = useAuth0();
  const authRef = useRef({ getAccessTokenSilently, loginWithRedirect, isAuthenticated });
  
  // Keep refs updated
  authRef.current = { getAccessTokenSilently, loginWithRedirect, isAuthenticated };

  useEffect(() => {
    // Only set up interceptors once
    if (interceptorsInitialized) {
      return;
    }
    interceptorsInitialized = true;

    // "Acting as" an agent was removed 2026-10-20 (one expert = one agent, which owns its brain).
    // Drop the selection an older build may have left, so nothing stale lingers in the browser.
    try { localStorage.removeItem('imaven_acting_agent'); } catch (e) { /* ignore */ }

    // Request interceptor - add auth token
    // PRIORITY: Use team member token if logged in, otherwise use Auth0 token
    const requestInterceptor = apiClient.interceptors.request.use(
      async (config) => {
        try {
          // Check if team member is authenticated - use their token
          if (isTeamMemberAuthenticated()) {
            const teamToken = getTeamToken();
            if (teamToken) {
              config.headers.Authorization = `Bearer ${teamToken}`;
              return config;
            }
          }
          
          // Fallback to Auth0 token (for experts/admins without team member login)
          const token = await authRef.current.getAccessTokenSilently();
          config.headers.Authorization = `Bearer ${token}`;
        } catch (error) {
          // Don't log error for guests - it's expected
          if (authRef.current.isAuthenticated) {
            console.error('Failed to get access token:', error);
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle auth errors
    const responseInterceptor = apiClient.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        // Safe access to error.response.status
        const status = error.response?.status;
        const errorCode = error.response?.data?.code;
        const errorMessage = error.response?.data?.error;

        // Out of AI tokens (402): surface globally so the app can open the purchase
        // screen / inform the user. Handled here AND by the global default-axios
        // interceptor (for calls that don't go through this client).
        if (status === 402 &&
            (errorCode === 'INSUFFICIENT_TOKENS' || errorMessage === 'insufficient_tokens')) {
          window.dispatchEvent(new CustomEvent('insufficient-tokens', { detail: error.response?.data || {} }));
          return Promise.reject(error);
        }

        // Debug logging for auth errors
        if (status === 401 || status === 403) {
          console.log('[AuthAxios] Auth error:', {
            status,
            errorCode,
            errorMessage,
            isTeamMember: isTeamMemberAuthenticated(),
            isAuth0: authRef.current.isAuthenticated,
            url: error.config?.url
          });
        }
        
        // Handle token issues - expired or invalid
        const isTokenIssue = errorCode === 'TOKEN_EXPIRED' || errorCode === 'INVALID_TOKEN';
        
        // Handle 401 with team member auth active - try to recover with Auth0
        if (status === 401 && isTeamMemberAuthenticated() && authRef.current.isAuthenticated) {
          // User has both Auth0 and team member auth - the team token is likely stale
          console.log('[AuthAxios] Team token issue, Auth0 is active - clearing stale team auth and retrying');
          clearTeamAuth();
          
          // Retry the request with Auth0 token
          try {
            const originalRequest = error.config;
            // Prevent infinite retry loop
            if (!originalRequest._retryWithAuth0) {
              originalRequest._retryWithAuth0 = true;
              const auth0Token = await authRef.current.getAccessTokenSilently();
              console.log('[AuthAxios] Retrying request with Auth0 token');
              originalRequest.headers.Authorization = `Bearer ${auth0Token}`;
              return apiClient.request(originalRequest);
            }
          } catch (retryError) {
            console.error('[AuthAxios] Failed to retry with Auth0 token:', retryError);
          }
          
          // If retry failed, reject the error
          return Promise.reject(error);
        }
        
        if (status === 401 && isTokenIssue) {
          // Team member only (no Auth0) - redirect to login
          if (isTeamMemberAuthenticated()) {
            clearTeamAuth();
            
            // Dispatch a custom event so the app can show a notification
            window.dispatchEvent(new CustomEvent('session-expired', {
              detail: { 
                message: errorMessage || 'Your session has expired. Please log in again.',
                type: 'teamMember'
              }
            }));
            
            // Redirect to login after a short delay to allow notification to show
            setTimeout(() => {
              window.location.href = '/login?expired=true';
            }, 100);
            
            return Promise.reject(error);
          }
          
          // For Auth0 users only, let Auth0 handle the re-authentication
          if (authRef.current.isAuthenticated) {
            window.dispatchEvent(new CustomEvent('session-expired', {
              detail: { 
                message: 'Your session has expired. Redirecting to login...',
                type: 'expert'
              }
            }));
            
            setTimeout(async () => {
              await authRef.current.loginWithRedirect();
            }, 100);
          }
          
          return Promise.reject(error);
        }
        
        if (status === 401 || status === 403) {
          // Only redirect to login if user was previously authenticated
          // Don't redirect for guests viewing the faded dashboard
          
          // Check if this is an invalid token error (not just permission denied)
          const isTokenError = errorCode === 'INVALID_TOKEN' || 
                               errorCode === 'TOKEN_EXPIRED';
          
          if (isTeamMemberAuthenticated() && !authRef.current.isAuthenticated && isTokenError) {
            // Team member with no Auth0 session and token issue - clear and redirect
            clearTeamAuth();
            window.location.href = '/login?expired=true';
          } else if (!authRef.current.isAuthenticated && !isTeamMemberAuthenticated()) {
            // No auth at all - redirect to Auth0
            await authRef.current.loginWithRedirect();
          }
          // If Auth0 is authenticated, don't redirect - just reject and let the component handle it
        } else if (status === 400) {
          // TODO: move to not allowed page
          console.warn('Bad request (400):', error.message);
        }
        
        return Promise.reject(error);
      }
    );

    // Cleanup function (only runs on unmount of last component using this)
    return () => {
      apiClient.interceptors.request.eject(requestInterceptor);
      apiClient.interceptors.response.eject(responseInterceptor);
      interceptorsInitialized = false;
    };
  }, []);

  return apiClient;
}

export default useAuthenticatedAxios;
